import React, { useReducer, useRef, useEffect, useState, useCallback } from 'react';
import MonthSelector from './MonthSelector';
import { parseDate } from '../HotSeller/Helpers';
import { INVOICE_PERIOD_FOR_HISTORICAL_DATA, seasonalPeriods, seasonalYearSpans, SELECT_MONTH, SELECT_PERIOD } from '../../ARSConstants';
import { SeasonalInvoicePeriod, SeasonalMonthsPeriodType, SeasonalState, TSetPriorityMode, SeasonalYearPeriodType } from '../../Types';
import { seasonalReducer } from './Reducer';
import { formatDateToYYYYMMDD } from 'Common/HelperClass';
import DateRangeWithoutYearPicker from './DateRangeWithoutYearPicker';
import { useDatePickerStyles } from '../HotSeller/DatePickerStyles';
import PeriodDropdown from '../HotSeller/PeriodDropdown';

type SeasonalProps = {
    setModePriority: TSetPriorityMode[];
    updateSetModePriority: (setModePriority: TSetPriorityMode[]) => void;
    onValidationChange?: (isValid: boolean) => void;
};

const Seasonal = ({ setModePriority, updateSetModePriority, onValidationChange }: SeasonalProps) => {
    const seasonalPriority = setModePriority.find((e) => e.mode === "seasonal");
    const datePickerCustomStyles = useDatePickerStyles();
    const selectedYearPeriodRef = useRef<SeasonalYearPeriodType>((seasonalPriority?.invoicePeriod as SeasonalInvoicePeriod)?.selectedPeriod || "Last 1 Year");
    const [selectedYearPeriod, setSelectedYearPeriod] = useState<SeasonalYearPeriodType>((seasonalPriority?.invoicePeriod as SeasonalInvoicePeriod)?.selectedPeriod || "Last 1 Year");
    const [isYearOpen, setIsYearOpen] = useState(false);

    const getInitialDates = () => {
        if (!seasonalPriority?.invoicePeriod) {
            return { fromDate: null, toDate: null };
        }
        const seasonalInvoice = seasonalPriority.invoicePeriod as SeasonalInvoicePeriod;
        const hasCustomDate = seasonalInvoice.selectedMonths?.includes('Custom Date');

        if (hasCustomDate && seasonalInvoice.startDate && seasonalInvoice.endDate) {
            return {
                fromDate: parseDate(seasonalInvoice.startDate),
                toDate: parseDate(seasonalInvoice.endDate)
            };
        }
        return { fromDate: null, toDate: null };
    };

    const { fromDate, toDate } = getInitialDates();

    const getInitialPeriods = (): SeasonalMonthsPeriodType[] | undefined => {
        if (!seasonalPriority?.invoicePeriod) return undefined;

        if ((seasonalPriority.invoicePeriod as any).mode === 'seasonal') {
            const seasonalInvoice = seasonalPriority.invoicePeriod as SeasonalInvoicePeriod;
            if (seasonalInvoice.selectedMonths && seasonalInvoice.selectedMonths.length > 0) {
                return seasonalInvoice.selectedMonths;
            }
        } else {
            if ((seasonalPriority.invoicePeriod as any).selectedMonths) {
                return (seasonalPriority.invoicePeriod as any).selectedMonths as SeasonalMonthsPeriodType[];
            }

            if (seasonalPriority.invoicePeriod.selectedPeriod === "Custom Date") {
                return ["Custom Date"];
            }
        }

        return undefined;
    };

    const initialState: SeasonalState = {
        selectedPeriod: getInitialPeriods(),
        isOpen: false,
        selectedFromDate: fromDate,
        selectedToDate: toDate
    };

    const [state, dispatch] = useReducer(seasonalReducer, initialState);
    const stateRef = useRef(state);

    const validateForm = useCallback((): boolean => {
        const isCustomDateSelected = state.selectedPeriod?.includes('Custom Date') || false;
        const hasBothDates = Boolean(state.selectedFromDate) && Boolean(state.selectedToDate);

        const isValid = !isCustomDateSelected || (isCustomDateSelected && hasBothDates);

        return isValid;
    }, [state.selectedPeriod, state.selectedFromDate, state.selectedToDate]);

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange(validateForm());
        }
    }, [validateForm, onValidationChange]);

    useEffect(() => {
        selectedYearPeriodRef.current = selectedYearPeriod;
    }, [selectedYearPeriod]);

    useEffect(() => {
        stateRef.current = state;
        selectedYearPeriodRef.current = selectedYearPeriod;

        if (state.selectedPeriod || state.selectedFromDate || state.selectedToDate || selectedYearPeriod) {
            const updatedPriorityModes = [...setModePriority];
            const seasonalIndex = updatedPriorityModes.findIndex(mode => mode.mode === "seasonal");

            if (seasonalIndex !== -1) {
                const isCustomDate = state.selectedPeriod?.includes('Custom Date') || false;

                const updatedInvoicePeriod: SeasonalInvoicePeriod = {
                    selectedPeriod: selectedYearPeriod,
                    selectedMonths: isCustomDate ? ["Custom Date"] : (state.selectedPeriod || []),
                    startDate: isCustomDate && state.selectedFromDate
                        ? formatDateToYYYYMMDD(state.selectedFromDate)
                        : '',
                    endDate: isCustomDate && state.selectedToDate
                        ? formatDateToYYYYMMDD(state.selectedToDate)
                        : ''
                };

                const currentInvoicePeriod = updatedPriorityModes[seasonalIndex].invoicePeriod as SeasonalInvoicePeriod;
                if (
                    !currentInvoicePeriod ||
                    currentInvoicePeriod.selectedPeriod !== updatedInvoicePeriod.selectedPeriod ||
                    JSON.stringify(currentInvoicePeriod.selectedMonths) !== JSON.stringify(updatedInvoicePeriod.selectedMonths) ||
                    currentInvoicePeriod.startDate !== updatedInvoicePeriod.startDate ||
                    currentInvoicePeriod.endDate !== updatedInvoicePeriod.endDate
                ) {
                    updatedPriorityModes[seasonalIndex] = {
                        ...updatedPriorityModes[seasonalIndex],
                        invoicePeriod: updatedInvoicePeriod
                    };
                    updateSetModePriority(updatedPriorityModes);
                }
            }
        }

    }, [state.selectedPeriod, state.selectedFromDate, state.selectedToDate, selectedYearPeriod, setModePriority, updateSetModePriority]);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const toggleYearDropdown = () => setIsYearOpen(!isYearOpen);

    const handleYearPeriodSelect = (period: SeasonalYearPeriodType) => {
        setSelectedYearPeriod(period);
        setIsYearOpen(false);
    };

    const handlePeriodToggle = (period: SeasonalMonthsPeriodType) => {
        if (period === "Custom Date") {
            dispatch({ type: 'CLOSE_DROPDOWN' });
        }
        dispatch({ type: 'TOGGLE_PERIOD', payload: period });
    };

    return (
        <>
            <style>{datePickerCustomStyles}</style>
            <div className="tw-box-border tw-mt-3 tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-lightgray-200" />
            <div className="tw-p-3">
                <div className="tw-justify-start tw-text-blue-950 tw-text-sm">{INVOICE_PERIOD_FOR_HISTORICAL_DATA}</div>

                <div className="tw-justify-start tw-text-gray-400 tw-text-xs tw-font-semibold tw-pt-4">{SELECT_MONTH}</div>
                <MonthSelector
                    isOpen={state.isOpen}
                    selectedPeriods={state.selectedPeriod || []}
                    onToggle={() => dispatch({ type: 'TOGGLE_DROPDOWN' })}
                    onSelect={handlePeriodToggle}
                    periods={seasonalPeriods}
                />

                {state.selectedPeriod?.includes('Custom Date') && (
                    <DateRangeWithoutYearPicker
                        fromDate={state.selectedFromDate}
                        toDate={state.selectedToDate}
                        onFromDateChange={(date) => dispatch({ type: 'SET_FROM_DATE', payload: date })}
                        onToDateChange={(date) => dispatch({ type: 'SET_TO_DATE', payload: date })}
                        fromDateClassName={!state.selectedFromDate ? "tw-outline-red-500" : ""}
                        toDateClassName={!state.selectedToDate ? "tw-outline-red-500" : ""}
                    />
                )}

                <div className="tw-justify-start tw-text-gray-400 tw-text-xs tw-font-semibold tw-pt-4">{SELECT_PERIOD}</div>
                <PeriodDropdown<SeasonalYearPeriodType>
                    isOpen={isYearOpen}
                    selectedPeriod={selectedYearPeriod}
                    onToggle={toggleYearDropdown}
                    onSelect={handleYearPeriodSelect}
                    periods={seasonalYearSpans}
                />
            </div>
        </>
    );
};

export default Seasonal;